import { 
  IMeetingDates, getHeightFromDate, getRoundedValue, getTimeValues, 
  getTopFromDate, setClass, throttle 
} from '@utils';
import { setHours, setMinutes } from 'date-fns';
import React, { 
  CSSProperties, DragEventHandler, FC, FocusEvent, 
  KeyboardEvent, MouseEvent, MouseEventHandler, ReactNode, 
  useEffect, useMemo, useRef, useState 
} from 'react';
import '../../styles/components/dndBlock.scss';

type TKeyboardAndFocusEvents = KeyboardEvent<HTMLDivElement> | FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>;
type TSetDate = (e: TKeyboardAndFocusEvents, mode: 'start' | 'end') => void;
interface DndBlockProps {
  start: Date;
  end: Date;
  color?: string;
  startHour: number;
  isCanMove?: boolean;
  className?: string;
  styles?: CSSProperties;
  isUpdateStatesAfterProps?: boolean;
  content: (objDates: IMeetingDates, setDate: TSetDate) => ReactNode;
  onChangePosition?: (start: Date, end: Date, e: TKeyboardAndFocusEvents | MouseEvent<HTMLDivElement>) => void;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDraggingEnd?: (start: Date, end: Date, e: MouseEvent<HTMLDivElement> | TKeyboardAndFocusEvents) => void;
  onMouseMoveDndWrapper?: (e: MouseEvent<HTMLDivElement>) => void;
}

export const DndBlock: FC<DndBlockProps> = ({
  start, end, color, startHour, isCanMove = true, className, 
  styles, isUpdateStatesAfterProps, content, onChangePosition, 
  onClick, onDraggingEnd, onMouseMoveDndWrapper,
}) => {

  const ref = useRef<HTMLDivElement>();
  const [top, setTop] = useState(getTopFromDate(start, startHour));
  const [height, setHeight] = useState(getHeightFromDate(start, end));
  const [isShowCopy, setIsShowCopy] = useState(false);
  const [leftForCopy, setLeftForCopy] = useState<number | string>(styles?.left ? styles.left : 'initial');

  const shiftY = useRef<number>();
  const cursorBeforeDragging = useRef<number>();
  const heightBeforeDragging = useRef<number>();
  const topBeforeDragging = useRef<number>();

  const isDragFull = start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 23 && end.getMinutes() === 59;

  useEffect(() => {
    setTop(getTopFromDate(start, startHour));
  }, [startHour]);

  useEffect(() => {
    isUpdateStatesAfterProps && setTop(getTopFromDate(start, startHour));
  }, [start, isUpdateStatesAfterProps]);

  useEffect(() => {
    isUpdateStatesAfterProps && setHeight(getHeightFromDate(start, end));
  }, [end, isUpdateStatesAfterProps]);

  const cbWrapper = (cb: (e: any) => void) => (e: any) => {
    throttle(() => {
      cb(e);
      if (onChangePosition) {
        const { startDate, endDate } = getTimeValues(start, end, ref.current.offsetTop, ref.current.offsetHeight, startHour);
        onChangePosition(startDate, endDate, e);
      }
    }, 200)();
  };

  const isCorrect = () =>
    // true - если нижний край элемента не заходит за нижний край родителя
    ref.current.offsetParent.getBoundingClientRect().bottom > ref.current.getBoundingClientRect().bottom;

  const onDragging = (e: MouseEvent) => {
    // расстояние от курсора до начала экрана - top родителя - прокрутка = новое значение top относительно родителя
    const newTopValue = e.pageY - ref.current.offsetParent.getBoundingClientRect().top - shiftY.current - window.scrollY;
    const isMovingToBottom = newTopValue > top;

    if (isMovingToBottom) {
      setTop(isCorrect()
        ? getRoundedValue(newTopValue, top)
        : ref.current.offsetParent.getBoundingClientRect().height - ref.current.getBoundingClientRect().height
      );
    }
    else {
      setTop(ref.current.offsetTop > 0
        ? getRoundedValue(newTopValue, top)
        : 0
      );
    }
  };

  const setEnd = (e: MouseEvent) => {
    //новая высота = начальная высота + разность позиций курсора текущего и до начала двигания
    const newHeightValue = heightBeforeDragging.current + e.clientY - cursorBeforeDragging.current;
    const isMovingToBottom = newHeightValue > height;
    document.body.classList.add('draggingEnd');
    if (isMovingToBottom && !isCorrect()) {
      // высота = высота родителя - top элемента. +1 нужен для нормальной работы условия isCorrect()
      setHeight(ref.current.offsetParent.getBoundingClientRect().height - ref.current.offsetTop + 1);
    }
    else {
      setHeight(getRoundedValue(newHeightValue, height, true));
    };
  };
  const saveDataBeforeDragging = (e: MouseEvent) => {
    e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    shiftY.current = e.clientY - rect.top;
    cursorBeforeDragging.current = e.clientY;
    heightBeforeDragging.current = rect.height;
    topBeforeDragging.current = ref.current.offsetTop;
    setIsShowCopy(true);
  };

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (isCanMove) {
      saveDataBeforeDragging(e);
      // если не сохранить в переменную, то removeEventListener не работает, думая, что это разные функции
      const cb = cbWrapper(setEnd);

      document.addEventListener('mousemove', cb);
      document.addEventListener('mouseup', () => {
        document.body.classList.remove('draggingEnd');
        document.onmouseup = null;
        setIsShowCopy(false);
        document.removeEventListener('mousemove', cb);
        const { startDate, endDate } = getTimeValues(start, end, ref.current?.offsetTop, ref.current?.getBoundingClientRect()?.height, startHour);
        onDraggingEnd && onDraggingEnd(startDate, endDate, e);
      }, { once: true });
    }
  };

  const onDragStart: DragEventHandler<HTMLDivElement> = (e) => {
    if (isCanMove) {
      saveDataBeforeDragging(e);
      e.dataTransfer.setDragImage(document.createElement('div'), 0, 0);
      styles?.left && setLeftForCopy(styles.left);
    }
  };

  // TODO: onDrag и onDragEnd оба двигают dnd и меняют время. Убрать дублирование функционала.
  const onDrag: DragEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    if (isCanMove && e.clientY !== 0 && e.screenY !== 0 && !isDragFull)
      cbWrapper(onDragging)(e);
  };

  const onDragEnd: DragEventHandler<HTMLDivElement> = (e) => {
    if (isCanMove && !isDragFull) {
      e.stopPropagation();
      cbWrapper(onDragging)(e);
      setIsShowCopy(false);
      const { startDate, endDate } = getTimeValues(start, end, ref.current.offsetTop, ref.current.getBoundingClientRect().height, startHour);
      onDraggingEnd && onDraggingEnd(startDate, endDate, e);
      styles?.left && setLeftForCopy(styles.left);
    }
  };

  const setDate = (e: TKeyboardAndFocusEvents, mode: 'start' | 'end') => {
    e.preventDefault();
    const hours = +(e.target as HTMLInputElement).value.slice(0, 2);
    const minutes = +(e.target as HTMLInputElement).value.slice(3);
    if (mode === 'start') {
      const newDate = setHours(setMinutes(start, minutes), hours);
      setTop(getTopFromDate(newDate, startHour));
      setHeight(getHeightFromDate(newDate, end));
      onChangePosition && onChangePosition(newDate, end, e);
      onDraggingEnd && onDraggingEnd(newDate, end, e);
    } else {
      const newEndDate = hours === 0 && minutes === 0 ? setHours(setMinutes(end, 59), 23) : setHours(setMinutes(end, minutes), hours);
      setHeight(getHeightFromDate(start, newEndDate));
      onChangePosition && onChangePosition(start, newEndDate, e);
      onDraggingEnd && onDraggingEnd(start, newEndDate, e);
    }
  };
  const computedClassName = useMemo(() =>
    setClass('dnd-wrapper', color && 'custom-color', !isCanMove && 'frozen', className && className)
    , [color, isCanMove, className]);

  return (
    <>
      <div
        className={setClass('dnd-wrapper', color && 'custom-color', !isCanMove && 'frozen', className && className)}
        style={{
          top,
          height,
          backgroundColor: color ? color : 'initial',
          ...styles
        }}
        ref={ref}
        onMouseMove={onMouseMoveDndWrapper}
      >
        <div
          className={'dnd-block'}
          draggable={isCanMove}
          onClick={onClick}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
        >
          {content(getTimeValues(start, end, top, height, startHour), setDate)}
        </div>
        {isCanMove && (
          <div
            className="end"
            onMouseDown={onMouseDown}
          />
        )}
      </div>
      <div
        className={`${computedClassName} copy`}
        style={{
          display: isShowCopy ? 'block' : 'none',
          top: topBeforeDragging.current,
          height: heightBeforeDragging.current,
          backgroundColor: color ? color : 'initial',
          width: styles?.width ? styles.width : 'initial',
          left: leftForCopy
        }}
      />
    </>
  );
};
